import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { apiLogin, apiRegister, apiLogout } from '../api/auth';
import type { LoginRequest, RegisterRequest } from '@codedrill/shared';

export function useAuth() {
  const user            = useAuthStore((s) => s.user);
  const accessToken     = useAuthStore((s) => s.accessToken);
  const setAuth         = useAuthStore((s) => s.setAuth);
  const clearAuth       = useAuthStore((s) => s.clearAuth);
  const setPendingTwoFA = useAuthStore((s) => s.setPendingTwoFA);
  const navigate        = useNavigate();

  const login = useCallback(
    async (body: LoginRequest, redirectTo = '/practice') => {
      const res = await apiLogin(body);

      if (res.requires2FA) {
        // Store the short-lived pending ticket, navigate to 2FA step
        setPendingTwoFA(res.pendingToken);
        navigate('/auth/2fa-login', { replace: true });
        return;
      }

      setAuth(res.user, res.accessToken);
      // Prompt for phone number after first login if not yet added
      const destination = res.user.phoneNumber ? redirectTo : '/auth/add-phone';
      navigate(destination, { replace: true });
    },
    [setAuth, setPendingTwoFA, navigate],
  );

  const register = useCallback(
    async (body: RegisterRequest) => {
      // Returns { message } — tokens not issued until email verified
      await apiRegister(body);
      // Navigate to a "check your email" confirmation screen
      navigate('/auth/verify-email-sent', { replace: true });
    },
    [navigate],
  );

  const logout = useCallback(async () => {
    await apiLogout().catch(() => {});
    clearAuth();
    navigate('/');
  }, [clearAuth, navigate]);

  return {
    user,
    isAuthed: accessToken !== null,
    login,
    register,
    logout,
  };
}
